<script lang="ts" setup>
import { KirbyStudentPreviewData } from "@/queries/student"
import { cleanHTML } from "@/utils/clean"
import { getObjectFocus, resolveStudentName } from "@/utils/student"
import { computed, ref, watch } from "vue"
import { useWindowSize } from "@vueuse/core"
import { useMotion } from "@vueuse/motion"
import { useSpring } from "@/composables/use-spring"

const props = defineProps<{
  student: KirbyStudentPreviewData
}>()

const studentName = computed(() => resolveStudentName(props.student))

// Card has a fixed height. Text occupies ~72px (h-18) at the top.
// Image is pinned to the bottom and grows upward to fill the card on hover.
const MOBILE_CONTAINER_HEIGHT = 320
const DESKTOP_CONTAINER_HEIGHT = 400
const TEXT_HEIGHT = 105

const { width } = useWindowSize()
const CONTAINER_HEIGHT = computed(() =>
  width.value < 640 ? MOBILE_CONTAINER_HEIGHT : DESKTOP_CONTAINER_HEIGHT,
)
const CLOSED_HEIGHT = computed(() => CONTAINER_HEIGHT.value - TEXT_HEIGHT)
const HOVER_HEIGHT = computed(() => CONTAINER_HEIGHT.value)

const imageRef = ref<HTMLElement>()
const isHovered = ref(false)
const { config } = useSpring("snappy")
const transition = { height: config }

const { apply } = useMotion(imageRef, {
  initial: { height: CLOSED_HEIGHT.value },
})

watch(CLOSED_HEIGHT, nextHeight => {
  apply({
    height: isHovered.value ? HOVER_HEIGHT.value : nextHeight,
    transition,
  })
})

const onMouseEnter = () => {
  isHovered.value = true
  apply({ height: HOVER_HEIGHT.value, transition })
}

const onMouseLeave = () => {
  isHovered.value = false
  apply({ height: CLOSED_HEIGHT.value, transition })
}
</script>

<template>
  <div
    class="border relative w-full bg-white text-sm overflow-hidden"
    :style="{ height: `${CONTAINER_HEIGHT}px` }"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="p-4 h-18">
      <div class="leading-default gap-4 grid grid-cols-2 items-start h-full">
        <div>
          <div class="line-clamp-1" v-html="cleanHTML(studentName)"></div>
          <div class="line-clamp-1" v-html="student.programme.title"></div>
          <p
            class="line-clamp-1"
            v-if="
              student.programme.title !== student.year &&
              student.year !== 'Foundation'
            "
            v-html="student.year"
          />
        </div>
        <div
          class="italic line-clamp-3"
          v-html="cleanHTML(student.title)"
        ></div>
      </div>
    </div>

    <router-link
      :to="`/${student.uri}`"
      class="absolute bottom-0 left-0 right-0 block cursor-pointer"
      aria-label="View student project"
    >
      <div ref="imageRef" class="w-full bg-black border-t">
        <img
          v-if="student.thumbnail"
          class="object-cover w-full h-full opacity-[.975]"
          :src="student.thumbnail.src"
          :srcset="student.thumbnail.srcset"
          :alt="student.thumbnail.alt"
          loading="lazy"
        />
      </div>
    </router-link>
  </div>
</template>
